import { CountryCode } from 'libphonenumber-js';

export interface CountryData {
  /** Country name (bilingual: English with Dutch in parentheses where different) */
  name: string;
  /** ISO 3166-1 alpha-2 code (uppercase) */
  code: CountryCode;
  /** Dial code without '+' prefix */
  dialCode: string;
  /** Example phone number in national format */
  example: string;
}

/**
 * Complete list of countries with bilingual names, ISO codes, dial codes,
 * and example phone numbers (national format).
 * Format: "English (Dutch)" where names differ, otherwise just the common name.
 * Sorted alphabetically by English name.
 */
export const ALL_COUNTRIES: CountryData[] = [
  { name: 'Afghanistan', code: 'AF', dialCode: '93', example: '070 123 4567' },
  { name: 'Albania (Albanië)', code: 'AL', dialCode: '355', example: '067 212 3456' },
  { name: 'Algeria (Algerije)', code: 'DZ', dialCode: '213', example: '0551 23 45 67' },
  {
    name: 'American Samoa (Amerikaans-Samoa)',
    code: 'AS',
    dialCode: '1684',
    example: '(684) 733-1234',
  },
  { name: 'Andorra', code: 'AD', dialCode: '376', example: '312 345' },
  { name: 'Angola', code: 'AO', dialCode: '244', example: '923 123 456' },
  { name: 'Anguilla', code: 'AI', dialCode: '1264', example: '(264) 235-1234' },
  {
    name: 'Antigua and Barbuda (Antigua en Barbuda)',
    code: 'AG',
    dialCode: '1268',
    example: '(268) 464-1234',
  },
  {
    name: 'Argentina (Argentinië)',
    code: 'AR',
    dialCode: '54',
    example: '011 15-2345-6789',
  },
  { name: 'Armenia (Armenië)', code: 'AM', dialCode: '374', example: '077 123456' },
  { name: 'Aruba', code: 'AW', dialCode: '297', example: '560 1234' },
  { name: 'Ascension', code: 'AC', dialCode: '247', example: '40123' },
  {
    name: 'Australia (Australië)',
    code: 'AU',
    dialCode: '61',
    example: '0412 345 678',
  },
  { name: 'Austria (Oostenrijk)', code: 'AT', dialCode: '43', example: '0664 123456' },
  {
    name: 'Azerbaijan (Azerbeidzjan)',
    code: 'AZ',
    dialCode: '994',
    example: '040 123 45 67',
  },
  {
    name: "Bahamas (Bahama's)",
    code: 'BS',
    dialCode: '1242',
    example: '(242) 359-1234',
  },
  { name: 'Bahrain (Bahrein)', code: 'BH', dialCode: '973', example: '3600 1234' },
  { name: 'Bangladesh', code: 'BD', dialCode: '880', example: '01812-345678' },
  { name: 'Barbados', code: 'BB', dialCode: '1246', example: '(246) 250-1234' },
  {
    name: 'Belarus (Wit-Rusland)',
    code: 'BY',
    dialCode: '375',
    example: '8 029 491-19-11',
  },
  { name: 'Belgium (België)', code: 'BE', dialCode: '32', example: '0450 00 12 34' },
  { name: 'Belize', code: 'BZ', dialCode: '501', example: '622-1234' },
  { name: 'Benin', code: 'BJ', dialCode: '229', example: '01 95 12 34 56' },
  { name: 'Bermuda', code: 'BM', dialCode: '1441', example: '(441) 370-1234' },
  { name: 'Bhutan', code: 'BT', dialCode: '975', example: '17 12 34 56' },
  { name: 'Bolivia', code: 'BO', dialCode: '591', example: '71234567' },
  {
    name: 'Bonaire, Sint Eustatius and Saba (Bonaire, Sint Eustatius en Saba)',
    code: 'BQ',
    dialCode: '599',
    example: '318 1234',
  },
  {
    name: 'Bosnia and Herzegovina (Bosnië en Herzegovina)',
    code: 'BA',
    dialCode: '387',
    example: '061 123 456',
  },
  { name: 'Botswana', code: 'BW', dialCode: '267', example: '71 123 456' },
  { name: 'Brazil (Brazilië)', code: 'BR', dialCode: '55', example: '(11) 96123-4567' },
  {
    name: 'British Virgin Islands (Britse Maagdeneilanden)',
    code: 'VG',
    dialCode: '1284',
    example: '(284) 300-1234',
  },
  { name: 'Brunei', code: 'BN', dialCode: '673', example: '712 3456' },
  { name: 'Bulgaria (Bulgarije)', code: 'BG', dialCode: '359', example: '043 012 345' },
  { name: 'Burkina Faso', code: 'BF', dialCode: '226', example: '70 12 34 56' },
  { name: 'Burundi', code: 'BI', dialCode: '257', example: '79 56 12 34' },
  { name: 'Cambodia (Cambodja)', code: 'KH', dialCode: '855', example: '091 234 567' },
  {
    name: 'Cameroon (Kameroen)',
    code: 'CM',
    dialCode: '237',
    example: '6 71 23 45 67',
  },
  { name: 'Canada', code: 'CA', dialCode: '1', example: '(506) 234-5678' },
  {
    name: 'Cape Verde (Kaapverdië)',
    code: 'CV',
    dialCode: '238',
    example: '991 12 34',
  },
  {
    name: 'Cayman Islands (Kaaimaneilanden)',
    code: 'KY',
    dialCode: '1345',
    example: '(345) 323-1234',
  },
  {
    name: 'Central African Republic (Centraal-Afrikaanse Republiek)',
    code: 'CF',
    dialCode: '236',
    example: '70 01 23 45',
  },
  { name: 'Chad (Tsjaad)', code: 'TD', dialCode: '235', example: '63 01 23 45' },
  { name: 'Chile (Chili)', code: 'CL', dialCode: '56', example: '(2) 2123 4567' },
  { name: 'China', code: 'CN', dialCode: '86', example: '131 2345 6789' },
  { name: 'Colombia', code: 'CO', dialCode: '57', example: '321 1234567' },
  { name: 'Comoros (Comoren)', code: 'KM', dialCode: '269', example: '321 23 45' },
  { name: 'Congo-Brazzaville', code: 'CG', dialCode: '242', example: '06 123 4567' },
  { name: 'Congo-Kinshasa', code: 'CD', dialCode: '243', example: '0991 234 567' },
  {
    name: 'Cook Islands (Cookeilanden)',
    code: 'CK',
    dialCode: '682',
    example: '71 234',
  },
  { name: 'Costa Rica', code: 'CR', dialCode: '506', example: '8312 3456' },
  {
    name: 'Croatia (Kroatië)',
    code: 'HR',
    dialCode: '385',
    example: '092 123 4567',
  },
  { name: 'Cuba', code: 'CU', dialCode: '53', example: '05 1234567' },
  { name: 'Curaçao', code: 'CW', dialCode: '599', example: '9 518 1234' },
  { name: 'Cyprus', code: 'CY', dialCode: '357', example: '96 123456' },
  { name: 'Czechia (Tsjechië)', code: 'CZ', dialCode: '420', example: '601 123 456' },
  { name: 'Denmark (Denemarken)', code: 'DK', dialCode: '45', example: '34 41 23 45' },
  { name: 'Djibouti', code: 'DJ', dialCode: '253', example: '77 83 10 01' },
  { name: 'Dominica', code: 'DM', dialCode: '1767', example: '(767) 225-1234' },
  {
    name: 'Dominican Republic (Dominicaanse Republiek)',
    code: 'DO',
    dialCode: '1809',
    example: '(809) 234-5678',
  },
  {
    name: 'East Timor (Oost-Timor)',
    code: 'TL',
    dialCode: '670',
    example: '7721 2345',
  },
  { name: 'Ecuador', code: 'EC', dialCode: '593', example: '099 123 4567' },
  { name: 'Egypt (Egypte)', code: 'EG', dialCode: '20', example: '010 01234567' },
  { name: 'El Salvador', code: 'SV', dialCode: '503', example: '7012 3456' },
  {
    name: 'Equatorial Guinea (Equatoriaal-Guinea)',
    code: 'GQ',
    dialCode: '240',
    example: '222 123 456',
  },
  { name: 'Eritrea', code: 'ER', dialCode: '291', example: '07 123 456' },
  { name: 'Estonia (Estland)', code: 'EE', dialCode: '372', example: '5123 4567' },
  { name: 'Eswatini', code: 'SZ', dialCode: '268', example: '7612 3456' },
  {
    name: 'Ethiopia (Ethiopië)',
    code: 'ET',
    dialCode: '251',
    example: '091 123 4567',
  },
  { name: 'Faroe Islands (Faeröer)', code: 'FO', dialCode: '298', example: '211234' },
  { name: 'Fiji', code: 'FJ', dialCode: '679', example: '701 2345' },
  { name: 'Finland', code: 'FI', dialCode: '358', example: '041 2345678' },
  {
    name: 'France (Frankrijk)',
    code: 'FR',
    dialCode: '33',
    example: '06 12 34 56 78',
  },
  {
    name: 'French Guiana (Frans-Guyana)',
    code: 'GF',
    dialCode: '594',
    example: '0694 20 12 34',
  },
  {
    name: 'French Polynesia (Frans-Polynesië)',
    code: 'PF',
    dialCode: '689',
    example: '87 12 34 56',
  },
  { name: 'Gabon', code: 'GA', dialCode: '241', example: '06 03 12 34' },
  { name: 'Gambia', code: 'GM', dialCode: '220', example: '301 2345' },
  { name: 'Georgia (Georgië)', code: 'GE', dialCode: '995', example: '555 12 34 56' },
  {
    name: 'Germany (Duitsland)',
    code: 'DE',
    dialCode: '49',
    example: '01512 3456789',
  },
  { name: 'Ghana', code: 'GH', dialCode: '233', example: '023 123 4567' },
  { name: 'Gibraltar', code: 'GI', dialCode: '350', example: '57123456' },
  { name: 'Greece (Griekenland)', code: 'GR', dialCode: '30', example: '691 234 5678' },
  { name: 'Greenland (Groenland)', code: 'GL', dialCode: '299', example: '22 12 34' },
  { name: 'Grenada', code: 'GD', dialCode: '1473', example: '(473) 403-1234' },
  { name: 'Guadeloupe', code: 'GP', dialCode: '590', example: '0690 00 12 34' },
  { name: 'Guam', code: 'GU', dialCode: '1671', example: '(671) 300-1234' },
  { name: 'Guatemala', code: 'GT', dialCode: '502', example: '5123 4567' },
  { name: 'Guernsey', code: 'GG', dialCode: '44', example: '07781 123456' },
  { name: 'Guinea (Guinee)', code: 'GN', dialCode: '224', example: '601 12 34 56' },
  {
    name: 'Guinea-Bissau (Guinee-Bissau)',
    code: 'GW',
    dialCode: '245',
    example: '955 012 345',
  },
  { name: 'Guyana', code: 'GY', dialCode: '592', example: '609 1234' },
  { name: 'Haiti (Haïti)', code: 'HT', dialCode: '509', example: '34 10 1234' },
  { name: 'Honduras', code: 'HN', dialCode: '504', example: '9123-4567' },
  { name: 'Hong Kong (Hongkong)', code: 'HK', dialCode: '852', example: '5123 4567' },
  {
    name: 'Hungary (Hongarije)',
    code: 'HU',
    dialCode: '36',
    example: '06 20 123 4567',
  },
  { name: 'Iceland (IJsland)', code: 'IS', dialCode: '354', example: '611 1234' },
  { name: 'India', code: 'IN', dialCode: '91', example: '081234 56789' },
  {
    name: 'Indonesia (Indonesië)',
    code: 'ID',
    dialCode: '62',
    example: '0812-345-678',
  },
  { name: 'Iran', code: 'IR', dialCode: '98', example: '0912 345 6789' },
  { name: 'Iraq (Irak)', code: 'IQ', dialCode: '964', example: '0791 234 5678' },
  { name: 'Ireland (Ierland)', code: 'IE', dialCode: '353', example: '085 012 3456' },
  { name: 'Israel (Israël)', code: 'IL', dialCode: '972', example: '050-234-5678' },
  { name: 'Italy (Italië)', code: 'IT', dialCode: '39', example: '312 345 6789' },
  {
    name: 'Ivory Coast (Ivoorkust)',
    code: 'CI',
    dialCode: '225',
    example: '01 23 45 6789',
  },
  { name: 'Jamaica', code: 'JM', dialCode: '1876', example: '(876) 210-1234' },
  { name: 'Japan', code: 'JP', dialCode: '81', example: '090-1234-5678' },
  { name: 'Jersey', code: 'JE', dialCode: '44', example: '07797 712345' },
  { name: 'Jordan (Jordanië)', code: 'JO', dialCode: '962', example: '07 9012 3456' },
  {
    name: 'Kazakhstan (Kazachstan)',
    code: 'KZ',
    dialCode: '7',
    example: '8 (771) 000 9998',
  },
  { name: 'Kenya (Kenia)', code: 'KE', dialCode: '254', example: '0712 123456' },
  { name: 'Kiribati', code: 'KI', dialCode: '686', example: '72001234' },
  { name: 'Kosovo', code: 'XK', dialCode: '383', example: '043 201 234' },
  { name: 'Kuwait (Koeweit)', code: 'KW', dialCode: '965', example: '500 12345' },
  {
    name: 'Kyrgyzstan (Kirgizië)',
    code: 'KG',
    dialCode: '996',
    example: '0700 123 456',
  },
  { name: 'Laos', code: 'LA', dialCode: '856', example: '020 23 123 456' },
  { name: 'Latvia (Letland)', code: 'LV', dialCode: '371', example: '21 234 567' },
  { name: 'Lebanon (Libanon)', code: 'LB', dialCode: '961', example: '71 123 456' },
  { name: 'Lesotho', code: 'LS', dialCode: '266', example: '5012 3456' },
  { name: 'Liberia', code: 'LR', dialCode: '231', example: '077 012 3456' },
  { name: 'Libya (Libië)', code: 'LY', dialCode: '218', example: '091-2345678' },
  { name: 'Liechtenstein', code: 'LI', dialCode: '423', example: '660 234 567' },
  {
    name: 'Lithuania (Litouwen)',
    code: 'LT',
    dialCode: '370',
    example: '(0-612) 34567',
  },
  {
    name: 'Luxembourg (Luxemburg)',
    code: 'LU',
    dialCode: '352',
    example: '628 123 456',
  },
  { name: 'Macau', code: 'MO', dialCode: '853', example: '6612 3456' },
  {
    name: 'Madagascar (Madagaskar)',
    code: 'MG',
    dialCode: '261',
    example: '032 12 345 67',
  },
  { name: 'Malawi', code: 'MW', dialCode: '265', example: '0991 23 45 67' },
  { name: 'Malaysia (Maleisië)', code: 'MY', dialCode: '60', example: '012-345 6789' },
  { name: 'Maldives (Maldiven)', code: 'MV', dialCode: '960', example: '771-2345' },
  { name: 'Mali', code: 'ML', dialCode: '223', example: '65 01 23 45' },
  { name: 'Malta', code: 'MT', dialCode: '356', example: '9696 1234' },
  {
    name: 'Marshall Islands (Marshalleilanden)',
    code: 'MH',
    dialCode: '692',
    example: '235-1234',
  },
  { name: 'Martinique', code: 'MQ', dialCode: '596', example: '0696 20 12 34' },
  {
    name: 'Mauritania (Mauritanië)',
    code: 'MR',
    dialCode: '222',
    example: '22 12 34 56',
  },
  { name: 'Mauritius', code: 'MU', dialCode: '230', example: '5251 2345' },
  { name: 'Mayotte', code: 'YT', dialCode: '262', example: '0639 01 23 45' },
  { name: 'Mexico', code: 'MX', dialCode: '52', example: '222 123 4567' },
  { name: 'Micronesia', code: 'FM', dialCode: '691', example: '350 1234' },
  {
    name: 'Moldova (Moldavië)',
    code: 'MD',
    dialCode: '373',
    example: '0621 12 345',
  },
  { name: 'Monaco', code: 'MC', dialCode: '377', example: '06 12 34 56 78' },
  { name: 'Mongolia (Mongolië)', code: 'MN', dialCode: '976', example: '8812 3456' },
  { name: 'Montenegro', code: 'ME', dialCode: '382', example: '060 123 456' },
  { name: 'Montserrat', code: 'MS', dialCode: '1664', example: '(664) 492-3456' },
  {
    name: 'Morocco (Marokko)',
    code: 'MA',
    dialCode: '212',
    example: '06 50 12 34 56',
  },
  { name: 'Mozambique', code: 'MZ', dialCode: '258', example: '82 123 4567' },
  { name: 'Myanmar', code: 'MM', dialCode: '95', example: '09 212 3456' },
  { name: 'Namibia (Namibië)', code: 'NA', dialCode: '264', example: '081 123 4567' },
  { name: 'Nauru', code: 'NR', dialCode: '674', example: '555 1234' },
  { name: 'Nepal', code: 'NP', dialCode: '977', example: '984-1234567' },
  {
    name: 'Netherlands (Nederland)',
    code: 'NL',
    dialCode: '31',
    example: '06 12345678',
  },
  {
    name: 'New Caledonia (Nieuw-Caledonië)',
    code: 'NC',
    dialCode: '687',
    example: '75.12.34',
  },
  {
    name: 'New Zealand (Nieuw-Zeeland)',
    code: 'NZ',
    dialCode: '64',
    example: '021 123 4567',
  },
  { name: 'Nicaragua', code: 'NI', dialCode: '505', example: '8123 4567' },
  { name: 'Niger', code: 'NE', dialCode: '227', example: '93 12 34 56' },
  { name: 'Nigeria', code: 'NG', dialCode: '234', example: '0802 123 4567' },
  { name: 'Niue', code: 'NU', dialCode: '683', example: '888 4012' },
  {
    name: 'Norfolk Island (Norfolkeiland)',
    code: 'NF',
    dialCode: '672',
    example: '3 81234',
  },
  {
    name: 'North Korea (Noord-Korea)',
    code: 'KP',
    dialCode: '850',
    example: '0192 123 4567',
  },
  {
    name: 'North Macedonia (Noord-Macedonië)',
    code: 'MK',
    dialCode: '389',
    example: '072 345 678',
  },
  {
    name: 'Northern Mariana Islands (Noordelijke Marianen)',
    code: 'MP',
    dialCode: '1670',
    example: '(670) 234-5678',
  },
  { name: 'Norway (Noorwegen)', code: 'NO', dialCode: '47', example: '40 61 23 45' },
  { name: 'Oman', code: 'OM', dialCode: '968', example: '9212 3456' },
  { name: 'Pakistan', code: 'PK', dialCode: '92', example: '0301 2345678' },
  { name: 'Palau', code: 'PW', dialCode: '680', example: '620 1234' },
  {
    name: 'Palestine (Palestina)',
    code: 'PS',
    dialCode: '970',
    example: '0599 123 456',
  },
  { name: 'Panama', code: 'PA', dialCode: '507', example: '6123-4567' },
  {
    name: 'Papua New Guinea (Papoea-Nieuw-Guinea)',
    code: 'PG',
    dialCode: '675',
    example: '7012 3456',
  },
  { name: 'Paraguay', code: 'PY', dialCode: '595', example: '0961 456789' },
  { name: 'Peru', code: 'PE', dialCode: '51', example: '912 345 678' },
  {
    name: 'Philippines (Filipijnen)',
    code: 'PH',
    dialCode: '63',
    example: '0905 123 4567',
  },
  { name: 'Poland (Polen)', code: 'PL', dialCode: '48', example: '512 345 678' },
  { name: 'Portugal', code: 'PT', dialCode: '351', example: '912 345 678' },
  { name: 'Puerto Rico', code: 'PR', dialCode: '1787', example: '(787) 234-5678' },
  { name: 'Qatar', code: 'QA', dialCode: '974', example: '3312 3456' },
  { name: 'Réunion', code: 'RE', dialCode: '262', example: '0692 12 34 56' },
  { name: 'Romania (Roemenië)', code: 'RO', dialCode: '40', example: '0712 034 567' },
  { name: 'Russia (Rusland)', code: 'RU', dialCode: '7', example: '8 (912) 345-67-89' },
  { name: 'Rwanda', code: 'RW', dialCode: '250', example: '0720 123 456' },
  {
    name: 'Saint Barthélemy',
    code: 'BL',
    dialCode: '590',
    example: '0690 00 12 34',
  },
  {
    name: 'Saint Kitts and Nevis (Saint Kitts en Nevis)',
    code: 'KN',
    dialCode: '1869',
    example: '(869) 765-2917',
  },
  { name: 'Saint Lucia', code: 'LC', dialCode: '1758', example: '(758) 284-5678' },
  { name: 'Saint Martin', code: 'MF', dialCode: '590', example: '0690 00 12 34' },
  {
    name: 'Saint Vincent and the Grenadines (Saint Vincent en de Grenadines)',
    code: 'VC',
    dialCode: '1784',
    example: '(784) 430-1234',
  },
  { name: 'Samoa', code: 'WS', dialCode: '685', example: '72 12345' },
  { name: 'San Marino', code: 'SM', dialCode: '378', example: '66 66 12 12' },
  {
    name: 'São Tomé and Príncipe (Sao Tomé en Principe)',
    code: 'ST',
    dialCode: '239',
    example: '981 2345',
  },
  {
    name: 'Saudi Arabia (Saoedi-Arabië)',
    code: 'SA',
    dialCode: '966',
    example: '051 234 5678',
  },
  { name: 'Senegal', code: 'SN', dialCode: '221', example: '70 123 45 67' },
  { name: 'Serbia (Servië)', code: 'RS', dialCode: '381', example: '060 1234567' },
  {
    name: 'Seychelles (Seychellen)',
    code: 'SC',
    dialCode: '248',
    example: '2 510 123',
  },
  { name: 'Sierra Leone', code: 'SL', dialCode: '232', example: '(025) 123456' },
  { name: 'Singapore', code: 'SG', dialCode: '65', example: '8123 4567' },
  { name: 'Sint Maarten', code: 'SX', dialCode: '1721', example: '(721) 520-5678' },
  { name: 'Sint-Helena', code: 'SH', dialCode: '290', example: '51234' },
  {
    name: 'Sint-Pierre and Miquelon (Sint-Pierre en Miquelon)',
    code: 'PM',
    dialCode: '508',
    example: '055 12 34',
  },
  {
    name: 'Slovakia (Slowakije)',
    code: 'SK',
    dialCode: '421',
    example: '0912 123 456',
  },
  { name: 'Slovenia (Slovenië)', code: 'SI', dialCode: '386', example: '031 234 567' },
  {
    name: 'Solomon Islands (Salomonseilanden)',
    code: 'SB',
    dialCode: '677',
    example: '74 21234',
  },
  { name: 'Somalia (Somalië)', code: 'SO', dialCode: '252', example: '7 1123456' },
  {
    name: 'South Africa (Zuid-Afrika)',
    code: 'ZA',
    dialCode: '27',
    example: '071 123 4567',
  },
  {
    name: 'South Korea (Zuid-Korea)',
    code: 'KR',
    dialCode: '82',
    example: '010-2000-0000',
  },
  {
    name: 'South Sudan (Zuid-Soedan)',
    code: 'SS',
    dialCode: '211',
    example: '0977 123 456',
  },
  { name: 'Spain (Spanje)', code: 'ES', dialCode: '34', example: '612 34 56 78' },
  { name: 'Sri Lanka', code: 'LK', dialCode: '94', example: '071 234 5678' },
  { name: 'Sudan (Soedan)', code: 'SD', dialCode: '249', example: '091 123 1234' },
  { name: 'Suriname', code: 'SR', dialCode: '597', example: '741-2345' },
  { name: 'Sweden (Zweden)', code: 'SE', dialCode: '46', example: '070-123 45 67' },
  {
    name: 'Switzerland (Zwitserland)',
    code: 'CH',
    dialCode: '41',
    example: '078 123 45 67',
  },
  { name: 'Syria (Syrië)', code: 'SY', dialCode: '963', example: '0944 567 890' },
  { name: 'Taiwan', code: 'TW', dialCode: '886', example: '0912 345 678' },
  {
    name: 'Tajikistan (Tadzjikistan)',
    code: 'TJ',
    dialCode: '992',
    example: '91 712 3456',
  },
  { name: 'Tanzania', code: 'TZ', dialCode: '255', example: '0621 234 567' },
  { name: 'Thailand', code: 'TH', dialCode: '66', example: '081 234 5678' },
  { name: 'Togo', code: 'TG', dialCode: '228', example: '90 11 23 45' },
  { name: 'Tokelau', code: 'TK', dialCode: '690', example: '7290' },
  { name: 'Tonga', code: 'TO', dialCode: '676', example: '771 5123' },
  {
    name: 'Trinidad and Tobago (Trinidad en Tobago)',
    code: 'TT',
    dialCode: '1868',
    example: '(868) 291-1234',
  },
  { name: 'Tunisia (Tunesië)', code: 'TN', dialCode: '216', example: '20 123 456' },
  { name: 'Turkey (Turkije)', code: 'TR', dialCode: '90', example: '0501 234 56 78' },
  { name: 'Turkmenistan', code: 'TM', dialCode: '993', example: '8 66 123456' },
  {
    name: 'Turks and Caicos Islands (Turks- en Caicoseilanden)',
    code: 'TC',
    dialCode: '1649',
    example: '(649) 231-1234',
  },
  { name: 'Tuvalu', code: 'TV', dialCode: '688', example: '90 1234' },
  { name: 'Uganda (Oeganda)', code: 'UG', dialCode: '256', example: '0712 345678' },
  { name: 'Ukraine (Oekraïne)', code: 'UA', dialCode: '380', example: '050 123 4567' },
  {
    name: 'United Arab Emirates (Verenigde Arabische Emiraten)',
    code: 'AE',
    dialCode: '971',
    example: '050 123 4567',
  },
  {
    name: 'United Kingdom (Verenigd Koninkrijk)',
    code: 'GB',
    dialCode: '44',
    example: '07400 123456',
  },
  {
    name: 'United States (Verenigde Staten)',
    code: 'US',
    dialCode: '1',
    example: '(201) 555-0123',
  },
  { name: 'Uruguay', code: 'UY', dialCode: '598', example: '094 231 234' },
  {
    name: 'Uzbekistan (Oezbekistan)',
    code: 'UZ',
    dialCode: '998',
    example: '91 234 56 78',
  },
  { name: 'Vanuatu', code: 'VU', dialCode: '678', example: '591 2345' },
  {
    name: 'Vatican City (Vaticaanstad)',
    code: 'VA',
    dialCode: '379',
    example: '312 345 6789',
  },
  { name: 'Venezuela', code: 'VE', dialCode: '58', example: '0412-1234567' },
  { name: 'Vietnam', code: 'VN', dialCode: '84', example: '0912 345 678' },
  {
    name: 'Wallis and Futuna (Wallis en Futuna)',
    code: 'WF',
    dialCode: '681',
    example: '82 12 34',
  },
  { name: 'Yemen (Jemen)', code: 'YE', dialCode: '967', example: '0712 345 678' },
  { name: 'Zambia', code: 'ZM', dialCode: '260', example: '095 5123456' },
  { name: 'Zimbabwe', code: 'ZW', dialCode: '263', example: '071 234 5678' },
];
